import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const SavedAddresses = ({
  savedAddresses,
  setSavedAddresses,
  showAddAddressModal,
  setShowAddAddressModal,
  addressForm,
  setAddressForm,
  editingAddressType,
  setEditingAddressType,
  user,
  setUser
}) => {

  const handleAddAddress = (type) => {
    setEditingAddressType(type);
    if (type === 'home' || type === 'work') {
      setAddressForm({
        label: type === 'home' ? 'Home' : 'Work',
        address: savedAddresses[type] || ''
      });
    } else {
      setAddressForm({
        label: '',
        address: ''
      });
    }
    setShowAddAddressModal(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.address.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an address',
        variant: 'destructive',
      });
      return;
    }

    try {
      let updatedAddresses = { ...savedAddresses };
      
      if (editingAddressType === 'home' || editingAddressType === 'work') {
        updatedAddresses[editingAddressType] = addressForm.address;
      } else {
        // Adding to favorites
        if (!addressForm.label.trim()) {
          toast({
            title: 'Error',
            description: 'Please enter a label for this address',
            variant: 'destructive',
          });
          return;
        }
        updatedAddresses.favorites = [
          ...(updatedAddresses.favorites || []),
          { label: addressForm.label, address: addressForm.address }
        ];
      }

      // Save to backend
      const api = (await import('../../utils/api')).default;
      await api.put('/auth/profile', {
        savedAddresses: updatedAddresses
      });

      setSavedAddresses(updatedAddresses);
      setUser({ ...user, savedAddresses: updatedAddresses });
      
      toast({
        title: 'Address saved',
        description: 'Your address has been saved successfully',
      });

      setShowAddAddressModal(false);
      setAddressForm({ label: '', address: '' });
      setEditingAddressType(null);
    } catch (error) {
      console.error('Error saving address:', error);
      toast({
        title: 'Error',
        description: 'Failed to save address',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteAddress = async (type, index = null) => {
    try {
      let updatedAddresses = { ...savedAddresses };
      
      if (type === 'home' || type === 'work') {
        updatedAddresses[type] = '';
      } else if (type === 'favorite' && index !== null) {
        updatedAddresses.favorites = updatedAddresses.favorites.filter((_, i) => i !== index);
      }

      // Save to backend
      const api = (await import('../../utils/api')).default;
      await api.put('/auth/profile', {
        savedAddresses: updatedAddresses
      });

      setSavedAddresses(updatedAddresses);
      setUser({ ...user, savedAddresses: updatedAddresses });
      
      toast({
        title: 'Address removed',
        description: 'Your address has been removed',
      });
    } catch (error) {
      console.error('Error removing address:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove address',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Saved Addresses</h2>
          <Button 
            variant="outline" 
            onClick={() => handleAddAddress('favorite')}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Favorite
          </Button>
        </div>

        <div className="space-y-3">
          {/* Home Address */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Home</p>
                {savedAddresses.home ? (
                  <p className="text-sm text-gray-600">{savedAddresses.home}</p>
                ) : (
                  <p className="text-sm text-gray-400">Not set</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleAddAddress('home')}
              >
                {savedAddresses.home ? 'Edit' : 'Add'}
              </Button>
              {savedAddresses.home && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteAddress('home')}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Work Address */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Work</p>
                {savedAddresses.work ? (
                  <p className="text-sm text-gray-600">{savedAddresses.work}</p>
                ) : (
                  <p className="text-sm text-gray-400">Not set</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleAddAddress('work')}
              >
                {savedAddresses.work ? 'Edit' : 'Add'}
              </Button>
              {savedAddresses.work && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteAddress('work')}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Favorite Addresses */}
          {savedAddresses.favorites && savedAddresses.favorites.length > 0 && (
            <>
              {savedAddresses.favorites.map((fav, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{fav.label}</p>
                      <p className="text-sm text-gray-600">{fav.address}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAddress('favorite', index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </>
          )}
        </div>
      </Card>

      {/* Add Address Modal */}
      <Dialog open={showAddAddressModal} onOpenChange={setShowAddAddressModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAddressType === 'home' ? 'Set Home Address' : 
               editingAddressType === 'work' ? 'Set Work Address' : 
               'Add Favorite Address'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {editingAddressType === 'favorite' && (
              <div className="space-y-2">
                <Label htmlFor="addressLabel">Label</Label>
                <Input
                  id="addressLabel"
                  placeholder="e.g., Gym, Mom's House"
                  value={addressForm.label}
                  onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="addressInput">Address</Label>
              <Input
                id="addressInput"
                placeholder="Enter full address"
                value={addressForm.address}
                onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                💡 Tip: Enter your complete address for best results when booking rides
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddAddressModal(false);
                  setAddressForm({ label: '', address: '' });
                  setEditingAddressType(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAddress}
                className="flex-1 bg-pink-600 hover:bg-pink-700"
              >
                Save Address
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SavedAddresses;